import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { DynamicStateApi, useUpdateDynamicStateMutation, useLazyGetDynamicStateQuery } from './DynamicStateApi';
import {
  DynamicStateScopes,
  type DynamicStateUser,
  type DynamicStateCompany,
  type DynamicStatePatch,
  type DynamicStateCompanyPatch,
} from './DynamicState';

type InitDynamicStateServiceResult = [
  () => void,
  {
    isUninitialized: boolean;
    isFetching: boolean;
  }
];

export function useInitService(): InitDynamicStateServiceResult {
  const [getDynamicState, { isUninitialized, isFetching }] = useLazyGetDynamicStateQuery();

  const initService = () => {
    if (isUninitialized) {
      getDynamicState();
    }
  };

  return [initService, { isUninitialized, isFetching }];
}

const selectDynamicState = DynamicStateApi.endpoints.getDynamicState.select();

export function useDynamicStateService<
  TUser extends DynamicStateUser = DynamicStateUser,
  TCompany extends DynamicStateCompany = DynamicStateCompany
>(): [{ user: TUser; company: TCompany } | undefined, (patch: DynamicStatePatch<TUser, TCompany>) => void] {
  const dispatch = useDispatch();
  const [updateDynamicStateRequest] = useUpdateDynamicStateMutation();

  const currentDynamicState = useSelector(createSelector(selectDynamicState, (dynamicState) => dynamicState.data));

  // Cast to generic types - feature hooks handle defaults for missing properties
  const typedState = currentDynamicState
    ? { user: currentDynamicState.user as TUser, company: currentDynamicState.company as TCompany }
    : undefined;

  const updateDynamicState = (patch: DynamicStatePatch<TUser, TCompany>) => {
    if (patch.scope === DynamicStateScopes.Company) {
      const companyState = patch.state as DynamicStateCompanyPatch<TCompany>;

      updateDynamicStateRequest({
        state: { ...companyState },
        scope: DynamicStateScopes.Company,
      });

      dispatch(
        DynamicStateApi.util.updateQueryData('getDynamicState', undefined, (draft) => {
          // Generic merge for company scope - handles any feature namespace

          (Object.keys(companyState) as Array<keyof TCompany>).forEach((featureKey) => {
            const featureState = companyState[featureKey];

            if (featureState && typeof featureState === 'object' && !Array.isArray(featureState)) {
              // Initialize the feature namespace if it doesn't exist
              // draft.company is typed as base DynamicStateCompany, so we need to use string key
              const featureKeyStr = featureKey as string;
              if (!draft.company[featureKeyStr]) {
                draft.company[featureKeyStr] = {} as Record<string, unknown>;
              }

              // Merge the feature state
              const existingState = draft.company[featureKeyStr] as Record<string, unknown>;
              Object.assign(existingState, featureState);
            }
          });
        })
      );
    }

    if (patch.scope === DynamicStateScopes.User) {
      updateDynamicStateRequest({
        state: { ...currentDynamicState?.user, ...patch.state },
        scope: DynamicStateScopes.User,
      });

      dispatch(
        DynamicStateApi.util.updateQueryData('getDynamicState', undefined, (draft) => {
          draft.user = { ...draft.user, ...patch.state };
        })
      );
    }
  };

  return [typedState, updateDynamicState];
}
